import type { VirtualItem } from '@tanstack/react-virtual';
import { type JSX, type PropsWithChildren } from 'react';

import { rowDetailsRowCSS } from './RowDetails.sty.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import { type Row } from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * @internal
 */
export function RowDetailsElement<TData extends DataTableV2RowData>(
  props: PropsWithChildren<{
    originalRow: Row<TData>;
    virtualRow: VirtualItem;
    rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
    rowParity: 'even' | 'odd';
    rowMeasure?: (node: Element | null) => void;
    isDraggedRow?: boolean;
  }>,
): JSX.Element {
  const {
    originalRow,
    virtualRow,
    rowSeparation,
    rowParity,
    rowMeasure,
    isDraggedRow = false,
    children,
  } = props;

  const table = useDataTableV2();
  const width =
    // subtract table border (left and right)
    (table.getVirtualizationContainer()?.offsetWidth ?? 4) - 4;

  return (
    <div
      role="row"
      id={originalRow.getRowDetailsAriaControlsId()}
      style={{
        ...originalRow.getGridRowPosition(virtualRow.index),
        ...originalRow.getRowDetailsGridPosition(),
        maxWidth: width,
        position: 'sticky',
        left: 0,
      }}
      className={rowDetailsRowCSS({
        rowSeparation,
        rowParity,
        isDraggedRow,
      })}
      ref={rowMeasure}
      data-rowid={originalRow.id}
      data-index={`row-${virtualRow.index}`}
      // only set the data-row attribute if the element should be considered for measuring (e.g. not in the drag overlay)
      data-row={rowMeasure ? virtualRow.index : undefined}
      data-column={0}
    >
      {children}
    </div>
  );
}
